/**
 * Which NC1 link-request watch the app shell should be running (App.vue,
 * 2026-10-14). Pure - no store, no keystore, no DOM - so
 * tests/LinkWatchPlan.test.mjs runs it under Node.
 *
 * Two watches exist, one per kind of browser:
 *   approver   the browser that HOLDS the account identity: lists pending
 *              enrollments once a minute, auto-approves the account's
 *              already-registered uploaders (UploaderAutoApprove.js) and
 *              shows the "Review" snackbar for the rest.
 *   requester  a browser WITHOUT the identity: resumes its own "Link a
 *              device" request so a grant given elsewhere still lands.
 *
 * Until 2026-10-14 the choice was made once, at page mount. An owner who set
 * up encryption from the Account page in that same tab (the account was not
 * NC1 when the tab loaded) or unlocked the browser later never got the
 * approver watch until the next full page load: their phones' enrollment
 * requests sat "waiting for the account owner" and only a reload fixed it
 * (dev, 2026-10-14). App.vue now re-plans on every account refresh and every
 * unlock; these two functions are that decision.
 */

/**
 * The watch that should run.
 *   configured   E2eeApi.isConfigured()
 *   nc1          account.e2ee.scheme === 'nc1'
 *   hasIdentity  this browser caches the account identity with its secrets
 * @returns {'approver'|'requester'|'none'}
 */
export function planLinkWatch ({ configured, nc1, hasIdentity } = {}) {
  if (!configured || !nc1) return 'none'
  return hasIdentity ? 'approver' : 'requester'
}

/**
 * What to do about the watches already running to reach `desired`. A watch
 * that is already the desired one is kept (never restarted); the other one
 * is stopped; nothing is started twice.
 * @returns {{stopApprover: boolean, stopRequester: boolean, start: 'approver'|'requester'|null}}
 */
export function reconcileLinkWatch ({ desired, approverRunning = false, requesterRunning = false } = {}) {
  return {
    stopApprover: !!approverRunning && desired !== 'approver',
    stopRequester: !!requesterRunning && desired !== 'requester',
    start: (desired === 'approver' && !approverRunning) || (desired === 'requester' && !requesterRunning)
      ? desired
      : null,
  }
}
